/**
 * Dealership Brand Configuration
 *
 * Defines which brands are available at each dealership location.
 * This ensures brands are shown in the UI even when they have 0 units.
 */

interface LocationBrands {
  newVehicleBrands: string[];
  usedVehicleBrands: string[];
}

/**
 * Brand configuration by location ID
 */
export const LOCATION_BRANDS: Record<number, LocationBrands> = {
  // Location 1 - Darling's Ford VW Audi
  1: {
    newVehicleBrands: ['Ford', 'VW', 'Audi', 'Fleet'],
    usedVehicleBrands: ['FVA', 'VC'],
  },

  // Location 2 - Darling's Honda Nissan Volvo
  2: {
    newVehicleBrands: ['Honda', 'Nissan', 'Volvo'],
    usedVehicleBrands: ['Used'],
  },

  // Location 3 - Darling's Auto Mall
  3: {
    newVehicleBrands: ['GM', 'Stellantis'],
    usedVehicleBrands: ['Used'],
  },

  // Location 4 - Darling's Chrysler Dodge Hyundai
  4: {
    newVehicleBrands: ['Hyundai', 'Stellantis'],
    usedVehicleBrands: ['Used'],
  },

  // Location 5 - Darling's Chevrolet
  5: {
    newVehicleBrands: ['Chevrolet'],
    usedVehicleBrands: ['Used'],
  },

  // Location 10 - Darling's Brunswick Ford
  10: {
    newVehicleBrands: ['Ford'],
    usedVehicleBrands: ['Used'],
  },

  // Location 11 - Darling's CDJR Newport
  11: {
    newVehicleBrands: ['Stellantis'],
    usedVehicleBrands: ['Used'],
  },

  // Location 12 - Darling's Green Point
  12: {
    newVehicleBrands: [],
    usedVehicleBrands: ['Used'],
  },

  // Location 14 - Darling's Kia
  14: {
    newVehicleBrands: ['Kia'],
    usedVehicleBrands: ['Used'],
  },

  // Location 44 - Darling's Undercoating & More
  44: {
    newVehicleBrands: [],
    usedVehicleBrands: [],
  },
};

/**
 * Get brands for a location
 */
export function getLocationBrands(locationId: number): LocationBrands {
  return LOCATION_BRANDS[locationId] || {
    newVehicleBrands: [],
    usedVehicleBrands: [],
  };
}

/**
 * Ensures all configured brands are present in the units object,
 * with 0 as the default for missing brands.
 */
export function ensureAllBrands(
  units: Record<string, number>,
  configuredBrands: string[]
): Record<string, number> {
  const result: Record<string, number> = {};

  // First add configured brands (in order)
  for (const brand of configuredBrands) {
    result[brand] = units[brand] ?? 0;
  }

  // Then add any additional brands from the data (shouldn't happen often)
  for (const [brand, count] of Object.entries(units)) {
    if (!(brand in result)) {
      result[brand] = count;
    }
  }

  return result;
}
